'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useDynamicTheme } from '@/components/DynamicThemeProvider';
import { useTheme } from '@/components/ThemeProvider';
import { getDeals } from '@/services/api';
import { getImageUrl } from '@/utils/serverUrl';
import PromoModal from '@/components/coupon/PromoModal';

function CollectionCard({
  item,
  primary,
  cardBg,
  borderClr,
  textColor,
  mutedText,
  accentColor,
  redeemLabel,
  onRedeem,
}: any) {
  const [index, setIndex] = useState(0);
  const total = item.coupons?.length || 0;
  const prev = () => setIndex((i) => (i - 1 + total) % total);
  const next = () => setIndex((i) => (i + 1) % total);

  if (total === 0) return null;

  const handleRedeem = (e: any) => {
    e.stopPropagation();
    const link = item.coupons[index]?.link || item.link || '';
    if (link) window.open(link, '_blank', 'noopener,noreferrer');
    onRedeem(item);
  };

  return (
    <div
      className="rounded-2xl overflow-hidden border shadow-sm hover:shadow-xl transition-all duration-300 group cursor-pointer"
      style={{ backgroundColor: cardBg, borderColor: borderClr }}
      onClick={handleRedeem}
    >
      <div className="flex items-center gap-3 px-4 pt-4 pb-2">
        {item.logo && (
          <div className="w-10 h-10 rounded-full overflow-hidden shadow-sm shrink-0">
            <img src={getImageUrl(item.logo, { width: 96 })} alt={item.title} loading="lazy" decoding="async" className="w-full h-full object-fill" />
          </div>
        )}
        <div className="min-w-0">
          <p className="font-bold text-sm" style={{ color: primary }}>
            {item.storeName} Coupons
          </p>
        </div>
      </div>
      <div className="relative mx-4 mb-4 rounded-xl overflow-hidden">
        {item.bg ? (
          /* height auto-fits the image — full image, no stretch, no crop */
          <img src={getImageUrl(item.bg, { width: 760 })} alt={item.title} loading="lazy" decoding="async" className="block w-full h-auto" />
        ) : (
          <div className="w-full h-[200px]" style={{ backgroundColor: `${primary}20` }} />
        )}
        <div
          className="absolute left-0 right-0 bottom-0 translate-y-full group-hover:translate-y-0 transition-transform duration-300 ease-in-out rounded-t-xl px-4 flex flex-col items-center justify-center"
          style={{ backgroundColor: cardBg, top: 10 }}
        >
          <p className="font-bold text-base text-center" style={{ color: primary }}>
            {item.coupons[index]?.badge || ''}
          </p>
          <p
            className="text-xs mt-1 text-center line-clamp-2 leading-relaxed"
            style={{ color: mutedText }}
          >
            {item.coupons[index]?.desc || ''}
          </p>
          {total > 1 && (
            <div className="flex items-center justify-center gap-2 mt-2">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  prev();
                }}
                className="w-6 h-6 rounded-full flex items-center justify-center border-none cursor-pointer"
                style={{ backgroundColor: borderClr, color: mutedText }}
              >
                <ChevronLeft className="w-3 h-3" />
              </button>
              <div className="flex gap-1.5">
                {item.coupons.map((_: any, i: number) => (
                  <span
                    key={i}
                    onClick={(e) => {
                      e.stopPropagation();
                      setIndex(i);
                    }}
                    className="h-2 rounded-full cursor-pointer transition-all"
                    style={{
                      width: i === index ? 20 : 8,
                      backgroundColor: i === index ? accentColor : borderClr,
                    }}
                  />
                ))}
              </div>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  next();
                }}
                className="w-6 h-6 rounded-full flex items-center justify-center border-none cursor-pointer"
                style={{ backgroundColor: borderClr, color: mutedText }}
              >
                <ChevronRight className="w-3 h-3" />
              </button>
            </div>
          )}
          <button
            onClick={handleRedeem}
            className="w-full mt-2 py-2 rounded-lg font-extrabold text-sm tracking-widest text-white transition-colors hover:opacity-90 cursor-pointer"
            style={{ backgroundColor: primary }}
          >
            {redeemLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function Collections() {
  const { siteConfig, darkPalette, labels } = useDynamicTheme();
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const primary = siteConfig?.theme?.primaryColor || '#7c3aed';
  const siteName = siteConfig?.siteName || 'CouponsFeast';
  const textColor = isDark ? darkPalette.text : siteConfig?.theme?.textColor || '#111827';
  const mutedText = isDark ? `${darkPalette.text}99` : `${textColor}80`;
  const cardBg = isDark ? darkPalette.cardBg : '#ffffff';
  const borderClr = isDark ? `${darkPalette.text}15` : `${textColor}12`;
  const sectionBg = isDark ? darkPalette.bg : '#f9fafb';
  const accentColor = siteConfig?.theme?.accentColor || '#84cc16';
  const collectionsTitle = labels?.homepage?.collectionsTitle || 'Collections';
  const redeemLabel = labels?.homepage?.redeemNow || 'REDEEM NOW';

  const [collections, setCollections] = useState<any[]>([]);
  const [modalData, setModalData] = useState<any>(null);

  useEffect(() => {
    getDeals({ section: 'collections' })
      .then((res) => {
        const data = res.data?.data ?? res.data ?? [];
        const deals = (Array.isArray(data) ? data : []).filter((d: any) => d.isActive);
        setCollections(
          deals.map((deal: any) => {
            const store = deal.store || {};
            const serverUrl = process.env.NEXT_PUBLIC_API_URL?.replace('/api', '') || 'http://localhost:5000';
            const logo = deal.logo || store.logo || '';
            const logoUrl = logo.startsWith('http') ? logo : logo ? `${serverUrl}${logo}` : '';
            const image = deal.image || '';
            const imageUrl = image.startsWith('http') ? image : image ? `${serverUrl}${image}` : '';
            
            return {
              title: deal.title,
              description: deal.description || '',
              logo: logoUrl,
              bg: imageUrl,
              link: deal.link || store.websiteUrl || '',
              discount: deal.discount || '',
              storeName: store.storeName || '',
              code: deal.couponCode || '',
              details: deal.description || '',
              expiryDate: deal.expiryDate || '',
              coupons: [
                {
                  badge: deal.discount || 'DEAL',
                  desc: deal.description || deal.title,
                  link: deal.link || '',
                },
              ],
            };
          })
        );
      })
      .catch(() => {});
  }, []);

  if (collections.length === 0) return null;

  return (
    <>
      <section className="py-8" style={{ backgroundColor: sectionBg }}>
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl md:text-2xl font-bold" style={{ color: textColor }}>
              {siteName} {collectionsTitle}
            </h2>
            <Link href="/coupons" className="flex items-center gap-1 text-sm font-bold no-underline transition-opacity hover:opacity-80" style={{ color: primary }}>
              View More Collections<ChevronRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {collections.slice(0, 4).map((item, i) => (
              <CollectionCard
                key={i}
                item={item}
                primary={primary}
                cardBg={cardBg}
                borderClr={borderClr}
                textColor={textColor}
                mutedText={mutedText}
                accentColor={accentColor}
                redeemLabel={redeemLabel}
                onRedeem={setModalData}
              />
            ))}
          </div>
        </div>
      </section>
      {modalData && (
        <PromoModal
          onClose={() => setModalData(null)}
          title={modalData.title}
          code={modalData.code}
          discount={modalData.discount}
          storeName={modalData.storeName}
          storeLogo={modalData.logo}
          storeUrl={modalData.link}
          expiryDate={modalData.expiryDate}
          details={modalData.details}
        />
      )}
    </>
  );
}
